// v7
// Path: frontend/src/components/DataCanvasV3/CanvasGrid.jsx
// Inputs: syntaxPath, instanceId, parentRowId, canvasConfig, setCanvasConfig, globalParameters, token, isStructureMode, targetSchemaGroup, onRemoveGrid
// Outputs: React Component rendering pure DIV grid (Data Mode & Structure Mode ERD view) with nested subgrid recursive instantiation.
// Summary: Core grid engine of Data Canvas V3. Enforces 100% unified Full Syntax Train column identity strings ([Device|IP|Inst|Port|User]Database->dbo.Table.Column) everywhere across Manage Columns popovers, drag-and-drop emitters, structural query builder blocks, and cell rendering. Applies regular font to native columns (colTablePath === syntaxPath) and italic font to foreign/injected columns (colTablePath !== syntaxPath). Zero short-key fallbacks.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const FS_COLUMNS = ['Name', 'Type', 'Extension', 'Size', 'DateModified', 'Path'];

export default function CanvasGrid({
    syntaxPath,
    instanceId,
    parentRowId = null,
    canvasConfig = {},
    setCanvasConfig,
    globalParameters = {},
    token,
    isStructureMode = false,
    targetSchemaGroup = 'Default',
    onRemoveGrid
}) {
    const isFsGrid = useMemo(() => {
        return syntaxPath && (syntaxPath.includes('FILE_SYSTEM::') || syntaxPath.includes('{FS}'));
    }, [syntaxPath]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [primaryKey, setPrimaryKey] = useState(null);

    const [columnWidths, setColumnWidths] = useState({});
    const [showManageColumns, setShowManageColumns] = useState(false);
    const [showAddSubgrid, setShowAddSubgrid] = useState(false);
    const [subgridSearchFilter, setSubgridSearchFilter] = useState('');
    const [columnSearchFilter, setColumnSearchFilter] = useState('');
    const [availableColumns, setAvailableColumns] = useState([]);
    const [availableRelationships, setAvailableRelationships] = useState([]);
    const [loadingCols, setLoadingCols] = useState(false);
    const [loadingRels, setLoadingRels] = useState(false);

    // inline editing state
    const [editCell, setEditCell] = useState(null); 
    const [savingCell, setSavingCell] = useState(false);

    // subgrid expansion state per row
    const [expandedRows, setExpandedRows] = useState({});

    // Drag-and-drop visual indicators
    const [dragOverZone, setDragOverZone] = useState(null); 
    const [dragOverSlot, setDragOverSlot] = useState(null);

    // Header title display path state: 0 = Full Absolute, 1 = Short Table, 2 = Connection Prefix, 3 = Database Name
    const [pathState, setPathState] = useState(0);

    const displayPathTitle = useMemo(() => {
        if (!syntaxPath) return '';
        if (isFsGrid) return syntaxPath;

        let connInfo = '';
        let clean = syntaxPath;

        if (syntaxPath.includes(']')) {
            const parts = syntaxPath.split(']');
            connInfo = parts[0] + ']';
            clean = parts.slice(1).join(']');
        }
        if (clean.startsWith('->')) clean = clean.substring(2);

        const cleanLastHop = clean.includes('->') ? clean.split('->').pop().replace(/\[.*?\]/g, '') : clean;

        if (pathState === 0) {
            return syntaxPath;
        } else if (pathState === 1) {
            return cleanLastHop;
        } else if (pathState === 2) {
            return clean;
        } else {
            return connInfo ? `${connInfo}${clean}` : clean;
        }
    }, [syntaxPath, pathState]);

    const [showControls, setShowControls] = useState(canvasConfig?.showAllControls !== undefined ? canvasConfig.showAllControls : true);

    useEffect(() => {
        if (canvasConfig?.showAllControls !== undefined) {
            setShowControls(canvasConfig.showAllControls);
        }
    }, [canvasConfig?.showAllControls]);

    // GridTemplate State
    const [resolvedConfig, setResolvedConfig] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [reloadTrigger, setReloadTrigger] = useState(0);
    const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateDesc, setNewTemplateDesc] = useState('');
    const [saveTemplateAction, setSaveTemplateAction] = useState('new'); 
    const [sqlConstructed, setSqlConstructed] = useState('');
    const [showSqlPopover, setShowSqlPopover] = useState(false);
    
    // Pagination and Sorting
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 50;
    const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

    const activeSchemaGroup = syntaxPath.includes(']') ? syntaxPath.split(']')[0].replace('[', '') : targetSchemaGroup;
    const configKey = instanceId || syntaxPath;
    const currentTable = syntaxPath.split('->').pop().replace(/\[.*?\]/, ''); 
    const activeSubgrids = canvasConfig?.levels?.[configKey]?.subgrids || [];
    const activeTemplateId = canvasConfig?.levels?.[configKey]?.gridTemplateId || null;

    // Sequential task queue (FIFO)
    const taskQueue = useRef(Promise.resolve());
    const enqueueTask = useCallback((taskFn) => {
        taskQueue.current = taskQueue.current.then(() => taskFn().catch(err => {
            console.error('[CanvasGrid Task Queue Error]:', err);
        }));
        return taskQueue.current;
    }, []);

    const triggerReload = () => setReloadTrigger(prev => prev + 1);

    // Columns selection: ALWAYS Full Syntax Train keys ([Device|IP|Inst|Port|User]Database->dbo.Table.Column)
    const columns = useMemo(() => {
        const levelConfig = canvasConfig?.levels?.[configKey];
        if (levelConfig && Array.isArray(levelConfig.columns)) {
            return levelConfig.columns;
        }
        if (resolvedConfig && Array.isArray(resolvedConfig.columns)) {
            return resolvedConfig.columns;
        }
        if (isFsGrid) {
            return FS_COLUMNS.map(c => `${syntaxPath}.${c}`);
        }
        return [];
    }, [canvasConfig, configKey, resolvedConfig, isFsGrid, syntaxPath]);

    // Fetch saved grid templates on mount
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await fetch(`/api/v3/grid-templates?targetPath=${encodeURIComponent(currentTable)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const tpls = await res.json();
                    setTemplates(Array.isArray(tpls) ? tpls : []);
                }
            } catch (err) {
                console.error("Error fetching templates:", err);
            }
        };
        if (token && currentTable) fetchTemplates();
    }, [token, currentTable]);

    // Fetch data whenever path, parameters, layout, page or sort change
    useEffect(() => {
        let isSubscribed = true;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const levelConfig = canvasConfig?.levels?.[configKey] || {};
                const reqBody = {
                    path: syntaxPath,
                    syntaxPath,
                    parentRowId,
                    filterConditions: levelConfig.filterConditions || [],
                    selectedColumns: columns,
                    parameters: globalParameters,
                    gridTemplateId: activeTemplateId,
                    page,
                    limit,
                    sortColumn: sortConfig.column,
                    sortDirection: sortConfig.direction
                };

                const res = await fetch('/api/v3/data', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(reqBody)
                });

                if (!res.ok) {
                    const errData = await res.json();
                    throw new Error(errData.error || errData.details || `HTTP ${res.status}`);
                }

                const result = await res.json();
                if (isSubscribed) {
                    setData(result.data || []);
                    setTotalCount(result.totalCount || (result.data ? result.data.length : 0));
                    setPrimaryKey(result.primaryKey || null);
                    setSqlConstructed(result.sqlConstructed || '');
                    if (result.resolvedConfig) {
                        setResolvedConfig(result.resolvedConfig);
                    }
                }
            } catch (err) {
                if (isSubscribed) {
                    setError(err.message);
                    setData([]);
                }
            } finally {
                if (isSubscribed) setLoading(false);
            }
        };

        enqueueTask(fetchData);

        return () => {
            isSubscribed = false;
        };
    }, [syntaxPath, parentRowId, JSON.stringify(canvasConfig?.levels?.[configKey]), JSON.stringify(globalParameters), token, page, sortConfig, reloadTrigger, enqueueTask]);

    const handleSort = (columnKey) => {
        setSortConfig(prev => {
            if (prev.column === columnKey) {
                return { column: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { column: columnKey, direction: 'asc' };
        });
    };

    const toggleRowExpand = (rowId) => {
        setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
    };

    const handleRemoveColumn = (colKeyToRemove) => {
        setCanvasConfig(prev => {
            const next = { ...prev, levels: { ...prev.levels } };
            const level = { ...(next.levels[configKey] || { subgrids: [] }) };
            let currentCols = [...(level.columns || columns || [])];
            level.columns = currentCols.filter(c => c !== colKeyToRemove);
            next.levels[configKey] = level;
            return next;
        });
    };

    // Column Toggle Helper — 100% unified code path for Manage Columns and drag-and-drop.
    // EVERY column key stored in level.columns is the FULL ABSOLUTE SYNTAX TRAIN string.
    const handleToggleColumnInGrid = (colKey) => {
        const colName = colKey.includes('.') ? colKey.split('.').pop() : colKey;
        const fullColKey = (colKey.includes('->') || colKey.startsWith('{FS}') || colKey.includes(']'))
            ? colKey
            : `${syntaxPath}.${colName}`;

        setCanvasConfig(prev => {
            const next = { ...prev, levels: { ...prev.levels } };
            const targetKey = (instanceId && next.levels[instanceId]) 
                ? instanceId 
                : ((syntaxPath && next.levels[syntaxPath]) ? syntaxPath : (configKey || instanceId || syntaxPath));
            
            const level = { ...(next.levels[targetKey] || { syntaxPath, columns: [], subgrids: [], displayStyle: 'default' }) };
            let currentCols = [...(level.columns || columns || [])];
            
            const existingIdx = currentCols.findIndex(c => c === fullColKey || c === colKey);
            if (existingIdx !== -1) {
                currentCols.splice(existingIdx, 1);
            } else {
                currentCols.push(fullColKey);
            }
            level.columns = currentCols;
            next.levels[targetKey] = level;
            return next;
        });
    };

    // Fetch Available Columns for Manage Columns Dropdown — Uses FULL SYNTAX TRAIN keys
    const fetchAvailableColumns = async (force = false) => {
        if (!force && availableColumns.length > 0) return;
        setLoadingCols(true);
        try {
            if (isFsGrid) {
                setAvailableColumns(FS_COLUMNS.map(c => `${syntaxPath}.${c}`));
                return;
            }
            const res = await fetch(`/api/v3/tables?targetPath=${encodeURIComponent(syntaxPath)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            let cols = [];
            if (Array.isArray(result)) {
                cols = result.map(c => typeof c === 'string' ? c : (c.COLUMN_NAME || c.columnName || c.name || c));
            } else if (result.columns && Array.isArray(result.columns)) {
                cols = result.columns.map(c => typeof c === 'string' ? c : (c.COLUMN_NAME || c.columnName || c.name || c));
            } else if (result.tables && result.tables.length > 0) {
                cols = result.tables.flatMap(t => (t.columns || []).map(c => typeof c === 'string' ? c : (c.COLUMN_NAME || c.name)));
            } else if (data[0]) {
                cols = Object.keys(data[0]).filter(k => !k.startsWith('_'));
            }
            
            // Build full Syntax Train keys ([Device|IP|Instance|Port|User]DatabaseName->dbo.TableName.ColumnName)
            const fullCols = cols.map(c => {
                const colName = typeof c === 'string' && c.includes('.') ? c.split('.').pop() : String(c);
                return `${syntaxPath}.${colName}`;
            });
            setAvailableColumns(fullCols);
        } catch (err) {
            console.error('Error fetching available columns:', err);
        } finally {
            setLoadingCols(false);
        }
    };

    useEffect(() => {
        if (syntaxPath) {
            fetchAvailableColumns(true);
        }
    }, [syntaxPath]);

    // Fetch Available Relationships for + Add Subgrid Dropdown
    const fetchAvailableRelationships = async () => {
        if (availableRelationships.length > 0) return;
        setLoadingRels(true);
        try {
            const targetNode = syntaxPath.split('->').pop().replace(/\[.*?\]/g, '');
            let schema = 'dbo';
            let table = targetNode;
            if (targetNode.includes('.')) {
                [schema, table] = targetNode.split('.');
            }
            const res = await fetch(`/api/v3/schema/relationships?schema=${schema}&table=${table}&targetPath=${encodeURIComponent(syntaxPath)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            const rels = [];

            // 1. Self-join of subject table FIRST!
            const pk = result.primaryKey || 'ID';
            rels.push({
                name: `${schema}.${table}`,
                type: 'self',
                isSelfJoin: true,
                path: `${syntaxPath}->[${pk}=${pk}]${schema}.${table}`,
                leftCol: pk,
                rightCol: pk
            });

            // 2. Add all FK & Virtual relationships
            const serverRels = (result.relationships || result || []);
            serverRels.forEach(r => {
                if (r.type === 'self') return;
                const relTarget = r.targetTable || r.childTable || r.tableName || r.table;
                const relSchema = r.targetSchema || r.childSchema || schema;
                const fullTarget = relTarget ? `${relSchema}.${relTarget}` : `${schema}.${table}`;
                rels.push({
                    name: fullTarget,
                    type: r.type || 'child',
                    path: r.path || `${syntaxPath}->${fullTarget}`,
                    leftCol: r.foreignKeyColumn || r.targetColumn || r.childColumn || 'ID',
                    rightCol: r.targetColumn || r.childColumn || r.foreignKeyColumn || 'ID'
                });
            });

            setAvailableRelationships(rels);
        } catch (err) {
            console.error('Error fetching relationships:', err);
        } finally {
            setLoadingRels(false);
        }
    };

    // Add Subgrid Helper — Strictly preserves Full Syntax Train prefix
    const handleAddSubgridFromList = (rel, initialColumns = []) => {
        let relPath = rel.path || '';
        if (!relPath || !relPath.startsWith('[')) {
            const rawTarget = rel.path || rel.name || String(rel);
            const hopSuffix = rawTarget.includes('->')
                ? rawTarget.substring(rawTarget.indexOf('->'))
                : `->${rawTarget}`;
            relPath = syntaxPath.endsWith(hopSuffix) ? syntaxPath : `${syntaxPath}${hopSuffix}`;
        }
        const newSubgridId = `subgrid_${Date.now()}`;
        
        setCanvasConfig(prev => {
            const next = { ...prev, levels: { ...(prev.levels || {}) } };
            const level = { ...(next.levels[configKey] || { subgrids: [] }) };
            const subgrids = (level.subgrids || []).map(sg => typeof sg === 'string' ? sg : (sg.instanceId || sg.id)).filter(Boolean);
            
            if (!subgrids.includes(newSubgridId)) {
                subgrids.push(newSubgridId);
            }
            
            level.subgrids = subgrids;
            if (!next.levels[newSubgridId]) {
                next.levels[newSubgridId] = {
                    syntaxPath: relPath,
                    columns: initialColumns || [],
                    subgrids: [],
                    displayStyle: 'default'
                };
            }
            next.levels[configKey] = level;
            return next;
        });
        setShowAddSubgrid(false);
    };

    const handleRemoveSubgrid = (subgridId) => {
        setCanvasConfig(prev => {
            const next = { ...prev, levels: { ...prev.levels } };
            const level = { ...(next.levels[configKey] || { subgrids: [] }) };
            level.subgrids = (level.subgrids || []).filter(sub => {
                const subTarget = typeof sub === 'string' ? sub : (sub.instanceId || sub.id || sub.syntaxPath || sub.path);
                return subTarget !== subgridId;
            });
            next.levels[configKey] = level;
            delete next.levels[subgridId];
            return next;
        });
    };

    const handleTemplateChange = (e) => {
        const selectedId = e.target.value;
        setCanvasConfig(prev => {
            const next = { ...prev, levels: { ...prev.levels } };
            const level = { ...(next.levels[configKey] || { subgrids: [] }) };
            level.gridTemplateId = selectedId === 'base' ? null : selectedId;
            next.levels[configKey] = level;
            return next;
        });
        triggerReload();
    };

    const handleSaveTemplate = async () => {
        if (!newTemplateName.trim()) {
            alert('Please enter a template name');
            return;
        }
        setSaving(true);
        try {
            const currentLayoutConfig = {
                columns,
                subgrids: activeSubgrids,
                displayStyle: resolvedConfig?.displayStyle || 'default',
                defaultSort: sortConfig.column ? `${sortConfig.column} ${sortConfig.direction}` : null
            };

            let res;
            if (saveTemplateAction === 'update' && activeTemplateId) {
                res = await fetch(`/api/v3/grid-templates/${activeTemplateId}`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Name: newTemplateName.trim() || undefined,
                        Description: newTemplateDesc.trim() || undefined,
                        ConfigurationJSON: currentLayoutConfig
                    })
                });
            } else {
                res = await fetch(`/api/v3/grid-templates`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: newTemplateName.trim(),
                        description: newTemplateDesc.trim(),
                        targetPath: currentTable,
                        connectionScope: activeSchemaGroup,
                        configuration: currentLayoutConfig,
                        isDefault: false
                    })
                });
            }

            if (!res.ok) throw new Error('Failed to save grid template');
            const savedTpl = await res.json();
            
            setCanvasConfig(prev => {
                const next = { ...prev, levels: { ...prev.levels } };
                const nextLevel = { ...(next.levels[configKey] || { subgrids: [] }) };
                nextLevel.gridTemplateId = savedTpl.GridTemplateID || savedTpl.id || activeTemplateId;
                next.levels[configKey] = nextLevel;
                return next;
            });

            setShowSaveTemplateModal(false);
            setNewTemplateName('');
            setNewTemplateDesc('');
            triggerReload();
        } catch (err) {
            alert(`Error saving template: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm("Are you sure you want to delete this saved template?")) return;
        try {
            const res = await fetch(`/api/v3/grid-templates/${templateId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to delete template');
            
            setCanvasConfig(prev => {
                const next = { ...prev, levels: { ...prev.levels } };
                const nextLevel = { ...(next.levels[configKey] || { subgrids: [] }) };
                if (nextLevel.gridTemplateId === templateId) {
                    nextLevel.gridTemplateId = null;
                }
                next.levels[configKey] = nextLevel;
                return next;
            });
            triggerReload();
        } catch (err) {
            alert(`Error deleting template: ${err.message}`);
        }
    };

    // Drag and drop handlers
    const handleHeaderDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverZone(null);
        try {
            const rawData = e.dataTransfer.getData('text/plain');
            if (!rawData) return;
            let parsed = null;
            try { parsed = JSON.parse(rawData); } catch(ex) { parsed = rawData; }
            
            let colToAdd = null;
            if (typeof parsed === 'object' && parsed !== null) {
                if (parsed.type === 'COLUMN_NODE') colToAdd = parsed.columnKey || `${syntaxPath}.${parsed.columnName}`;
                else if (parsed.type === 'TABLE_NODE') colToAdd = `${syntaxPath}.ID`;
            } else if (typeof parsed === 'string') {
                colToAdd = parsed;
            }
            if (colToAdd) {
                handleToggleColumnInGrid(colToAdd);
            }
        } catch (err) {
            console.error('Error dropping on header:', err);
        }
    };

    const handleDropColumnAtSlot = (e, targetIndex) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverZone(null);
        setDragOverSlot(null);

        let dragData = null;
        try {
            const raw = e.dataTransfer.getData('text/plain');
            if (raw) dragData = JSON.parse(raw);
        } catch (err) {}

        if (!dragData) return;

        if (dragData.type === 'COLUMN_NODE') {
            const colKey = dragData.columnKey || `${syntaxPath}.${dragData.columnName}`;
            if (!colKey) return;

            setCanvasConfig(prev => {
                const next = { ...prev, levels: { ...prev.levels } };
                const level = { ...(next.levels[configKey] || { columns: [], subgrids: [] }) };
                let existingCols = [...(level.columns || columns || [])];
                
                const existingIdx = existingCols.indexOf(colKey);
                if (existingIdx !== -1) {
                    existingCols.splice(existingIdx, 1);
                }
                
                existingCols.splice(targetIndex, 0, colKey);
                level.columns = existingCols;
                next.levels[configKey] = level;
                return next;
            });
        }
    };

    const handleSubgridBodyDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverZone(null);
        try {
            const rawData = e.dataTransfer.getData('text/plain');
            if (!rawData) return;
            let parsed = null;
            try { parsed = JSON.parse(rawData); } catch(ex) { parsed = rawData; }
            
            let targetSchema = 'dbo';
            let targetTable = '';
            let fullRelPath = null;
            let initialCols = [];

            if (typeof parsed === 'object' && parsed !== null) {
                targetSchema = parsed.schema || 'dbo';
                targetTable = parsed.table;
                if (parsed.path && parsed.path.includes('->')) {
                    fullRelPath = parsed.path;
                }
                if (parsed.type === 'COLUMN_NODE') {
                    const colKey = parsed.columnKey || `${syntaxPath}.${parsed.columnName}`;
                    if (colKey) initialCols.push(colKey);
                }
            } else if (typeof parsed === 'string') {
                targetTable = parsed.includes('.') ? parsed.split('.').pop() : parsed;
            }

            if (!targetTable && fullRelPath) {
                targetTable = fullRelPath.split('->').pop().replace(/\[.*?\]/g, '').split('.').pop();
            }

            if (!targetTable) return;

            const pk = primaryKey || 'ID';
            let subgridPath = fullRelPath;

            if (!subgridPath) {
                subgridPath = `${syntaxPath}->[${pk}=${pk}]${targetSchema}.${targetTable}`;
            }

            handleAddSubgridFromList({
                name: `${targetSchema}.${targetTable}`,
                path: subgridPath,
                leftCol: pk,
                rightCol: pk
            }, initialCols);
        } catch (err) {
            console.error('Error dropping on subgrid zone:', err);
        }
    };

    const handleResizeMouseDown = (e, col) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = columnWidths[col] || 150;

        const onMouseMove = (moveEvent) => {
            const newWidth = Math.max(80, startWidth + (moveEvent.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [col]: newWidth }));
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / limit));

    return (
        <div 
            className={`canvas-grid my-3 transition-all duration-200 overflow-visible ${
                isStructureMode 
                    ? 'border-2 border-amber-500/80 bg-slate-950 shadow-[0_0_30px_rgba(245,158,11,0.25)] rounded-xl' 
                    : 'border border-gray-200 bg-white shadow-sm rounded-md'
            }`}
        >
            {/* Header & Controls Toolbar */}
            <div className={`px-4 py-2.5 border-b flex items-center justify-between transition-colors ${
                isStructureMode 
                    ? 'bg-gradient-to-r from-slate-950 via-slate-900 to-amber-950/60 border-amber-500/60 text-amber-100' 
                    : 'bg-gray-50 border-gray-200 text-gray-800'
            }`}>
                <div className="flex items-center space-x-2 truncate">
                    {onRemoveGrid && (
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveGrid(configKey);
                            }}
                            className="bg-red-500/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/30 font-bold px-2 py-0.5 rounded transition-all text-xs flex items-center justify-center cursor-pointer shadow-sm"
                            title="Remove Grid from Canvas Workspace"
                        >
                            ✕
                        </button>
                    )}
                    <span className="text-base font-bold truncate tracking-tight font-mono" title={`Full Syntax Train: ${syntaxPath}`}>
                        {displayPathTitle}
                    </span>
                    <div 
                        className="relative inline-block ml-2 flex-shrink-0"
                        onMouseEnter={() => setShowSqlPopover(true)}
                        onMouseLeave={() => setShowSqlPopover(false)}
                    >
                        <button 
                            type="button"
                            title={sqlConstructed || "Constructed SQL query (pending/loading)"}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowSqlPopover(prev => !prev);
                            }}
                            className="cursor-pointer text-[10px] bg-slate-900 hover:bg-slate-800 text-emerald-400 border border-emerald-500/60 px-2 py-0.5 rounded font-mono font-bold shadow transition-all flex items-center space-x-1"
                        >
                            <span>⚡</span>
                            <span>SQL QUERY</span>
                        </button>
                        {showSqlPopover && (
                            <div className="absolute left-0 top-full mt-2 z-[9999] w-[550px] p-3.5 bg-slate-950 text-emerald-300 font-mono text-[11px] rounded-lg border border-emerald-500/40 shadow-2xl whitespace-pre-wrap break-all max-h-80 overflow-y-auto">
                                <div className="text-slate-300 font-sans text-[11px] border-b border-slate-800 pb-1.5 mb-2 font-bold flex justify-between items-center">
                                    <span className="text-sky-400 font-bold">Constructed SQL Query</span>
                                    <span className="text-emerald-400 font-bold text-[10px] bg-emerald-950 px-1.5 py-0.5 rounded border border-emerald-800">LIVE EXECUTED</span>
                                </div>
                                <code className="text-emerald-300 select-text leading-relaxed font-mono block whitespace-pre-wrap">
                                    {sqlConstructed || '-- Query construction pending or loading...'}
                                </code>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-1">
                        <button 
                            onClick={() => setPathState(prev => (prev - 1 + 4) % 4)}
                            className={`text-xs px-1 py-0.5 rounded font-mono font-bold ${isStructureMode ? 'text-amber-400 hover:bg-amber-900/60' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                            title="Previous Path Display State"
                        >
                            ◄
                        </button>
                        <button 
                            onClick={() => setPathState(prev => (prev + 1) % 4)}
                            className={`text-xs px-1 py-0.5 rounded font-mono font-bold ${isStructureMode ? 'text-amber-400 hover:bg-amber-900/60' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                            title="Next Path Display State"
                        >
                            ►
                        </button>
                    </div>

                    <button 
                        onClick={() => setShowAddSubgrid(true)}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs px-2.5 py-1 rounded transition-colors shadow-sm ml-2"
                        title="Add child or parent subgrid"
                    >
                        + Add Subgrid
                    </button>
                </div>

                <div className="flex items-center space-x-2">
                    <button 
                        onClick={() => setShowControls(prev => !prev)}
                        className="text-xs text-gray-500 hover:text-gray-800 px-1.5 py-0.5 rounded hover:bg-gray-200 transition-colors"
                        title="Toggle per-grid builder controls section"
                    >
                        ⚙
                    </button>

                    {showControls && (
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1.5 border-l border-gray-300 pl-2">
                                <label className="text-xs font-bold text-gray-500">Template:</label>
                                <select 
                                    value={activeTemplateId || 'base'} 
                                    onChange={handleTemplateChange}
                                    className="text-xs bg-white border border-gray-300 text-gray-700 px-2 py-1 rounded outline-none shadow-sm focus:ring-1 focus:ring-blue-500 font-medium"
                                >
                                    <option value="base">Reset to Base (SELECT *)</option>
                                    {templates.map(t => (
                                        <option key={t.GridTemplateID} value={t.GridTemplateID}>
                                            {t.Name} {t.IsDefault ? '★' : ''}
                                        </option>
                                    ))}
                                </select>
                                {activeTemplateId && (
                                    <button 
                                        onClick={() => handleDeleteTemplate(activeTemplateId)}
                                        className="text-xs text-red-500 hover:text-red-700 p-1 font-bold"
                                        title="Delete selected template"
                                    >
                                        🗑️
                                    </button>
                                )}
                                <button 
                                    onClick={() => setShowSaveTemplateModal(true)}
                                    className="text-xs bg-blue-50 border border-blue-200 hover:border-blue-500 text-blue-700 hover:text-blue-900 px-2.5 py-1 rounded transition-colors shadow-sm font-semibold"
                                    title="Save current layout as a reusable template"
                                >
                                    Save Template
                                </button>
                            </div>

                            {/* + Add Subgrid Button & Dropdown Popover */}
                            <div className="relative inline-block">
                                <button 
                                    onClick={() => {
                                        setShowAddSubgrid(prev => !prev);
                                        setShowManageColumns(false);
                                        if (!showAddSubgrid) fetchAvailableRelationships();
                                    }}
                                    className="text-xs bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 text-gray-600 px-2 py-1 rounded transition-colors shadow-sm font-medium flex items-center space-x-1"
                                    title="Add a nested subgrid under this table"
                                >
                                    <span>+ Add Subgrid</span>
                                    <span className="text-[10px]">▼</span>
                                </button>
                                {showAddSubgrid && (
                                    <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-300 rounded-lg shadow-xl z-50 p-3 text-xs">
                                        <div className="font-bold text-gray-800 border-b pb-1 mb-2 flex justify-between items-center">
                                            <span>Add Subgrid for {currentTable}</span>
                                            <button onClick={() => setShowAddSubgrid(false)} className="text-gray-400 hover:text-gray-700 font-bold">✕</button>
                                        </div>
                                        <input 
                                            type="text" 
                                            placeholder="Search related tables..." 
                                            value={subgridSearchFilter} 
                                            onChange={(e) => setSubgridSearchFilter(e.target.value)} 
                                            className="w-full border border-slate-300 rounded px-2 py-1 mb-2 text-xs text-slate-900 bg-slate-50 focus:ring-2 focus:ring-sky-500 focus:outline-none font-medium placeholder-slate-400"
                                        />
                                        {loadingRels ? (
                                            <div className="text-gray-500 italic py-2 text-center">Loading related tables...</div>
                                        ) : availableRelationships.length === 0 ? (
                                            <div className="text-gray-400 italic py-2 text-center">No related tables found</div>
                                        ) : (
                                            <div className="max-h-56 overflow-y-auto space-y-1">
                                                {availableRelationships
                                                    .filter(rel => (rel.name || '').toLowerCase().includes(subgridSearchFilter.toLowerCase()))
                                                    .map((rel, idx) => {
                                                        const icon = rel.type === 'parent' ? '⬆️' : (rel.type === 'self' || rel.isSelfJoin) ? '➡️' : '⬇️';
                                                        return (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleAddSubgridFromList(rel)}
                                                                className="w-full text-left p-1.5 hover:bg-sky-50 text-slate-800 hover:text-sky-900 font-medium rounded flex items-center space-x-2 transition-colors border-b border-slate-100 last:border-0"
                                                            >
                                                                <span className="text-xs flex-shrink-0">{icon}</span>
                                                                <span className="truncate text-xs font-semibold">{rel.name}</span>
                                                            </button>
                                                        );
                                                    })
                                                }
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Manage Columns Button & Dropdown Popover */}
                            <div className="relative inline-block">
                                <button 
                                    onClick={() => {
                                        setShowManageColumns(prev => !prev);
                                        setShowAddSubgrid(false);
                                        if (!showManageColumns) fetchAvailableColumns();
                                    }}
                                    className="text-xs bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 text-gray-600 px-2 py-1 rounded transition-colors shadow-sm font-medium flex items-center space-x-1"
                                    title="Manage Columns"
                                >
                                    <span>Manage Columns</span>
                                    <span className="text-[10px]">▼</span>
                                </button>
                                {showManageColumns && (
                                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-300 rounded-lg shadow-xl z-50 p-3 text-xs">
                                        <div className="font-bold text-gray-800 border-b pb-1 mb-2 flex justify-between items-center">
                                            <span>Manage Columns ({currentTable})</span>
                                            <button onClick={() => setShowManageColumns(false)} className="text-gray-400 hover:text-gray-700 font-bold">✕</button>
                                        </div>
                                        <input 
                                            type="text" 
                                            placeholder="Search columns..." 
                                            value={columnSearchFilter} 
                                            onChange={(e) => setColumnSearchFilter(e.target.value)} 
                                            className="w-full border border-slate-300 rounded px-2 py-1 mb-2 text-xs text-slate-900 bg-slate-50 focus:ring-2 focus:ring-sky-500 focus:outline-none font-medium placeholder-slate-400"
                                        />
                                        <div className="flex justify-between items-center mb-2 px-1 text-[11px] border-b border-slate-100 pb-1">
                                            <button 
                                                onClick={() => {
                                                    setCanvasConfig(prev => {
                                                        const next = { ...prev, levels: { ...prev.levels } };
                                                        const level = { ...(next.levels[configKey] || { subgrids: [] }) };
                                                        level.columns = [...availableColumns];
                                                        next.levels[configKey] = level;
                                                        return next;
                                                    });
                                                }}
                                                className="text-sky-600 hover:text-sky-800 font-semibold"
                                            >
                                                ✓ Select All
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setCanvasConfig(prev => {
                                                        const next = { ...prev, levels: { ...prev.levels } };
                                                        const level = { ...(next.levels[configKey] || { subgrids: [] }) };
                                                        level.columns = [];
                                                        next.levels[configKey] = level;
                                                        return next;
                                                    });
                                                }}
                                                className="text-red-600 hover:text-red-800 font-semibold"
                                            >
                                                ✕ Clear All
                                            </button>
                                        </div>
                                        {loadingCols ? (
                                            <div className="text-gray-500 italic py-2 text-center">Loading columns...</div>
                                        ) : (
                                            <div className="max-h-56 overflow-y-auto space-y-1">
                                                {availableColumns
                                                    .filter(c => {
                                                        const name = typeof c === 'string' ? c : (c.name || c.key || '');
                                                        return name.toLowerCase().includes(columnSearchFilter.toLowerCase());
                                                    })
                                                    .map((col, idx) => {
                                                        const colKey = typeof col === 'string' ? col : (col.key || col.name);
                                                        const colName = colKey.split('.').pop();
                                                        const isChecked = columns.includes(colKey);
                                                        return (
                                                            <label key={idx} className="flex items-center space-x-2 p-1 hover:bg-sky-50 rounded cursor-pointer select-none" title={`Full Syntax Train: ${colKey}`}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={isChecked} 
                                                                    onChange={() => handleToggleColumnInGrid(colKey)} 
                                                                    className="rounded text-sky-600 focus:ring-sky-500"
                                                                />
                                                                <span className="text-slate-800 font-medium truncate font-mono text-[11px]">{colName}</span>
                                                            </label>
                                                        );
                                                    })
                                                }
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <div className="p-3 bg-red-50 border-b border-red-200 text-red-700 text-xs flex justify-between items-center font-medium">
                    <span>⚠️ Error loading grid data: {error}</span>
                    <button onClick={triggerReload} className="bg-red-100 hover:bg-red-200 text-red-800 px-2 py-0.5 rounded transition-colors font-bold">
                        Retry
                    </button>
                </div>
            )}

            {/* Grid Container */}
            <div className="relative overflow-x-auto min-h-[120px]">
                {loading && data.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-xs flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading grid data across syntax train...</span>
                    </div>
                ) : (
                    <div className="min-w-full inline-block align-middle">
                        {/* Table Header Row */}
                        <div 
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverZone('header');
                            }}
                            onDragLeave={() => setDragOverZone(null)}
                            onDrop={handleHeaderDrop}
                            className={`flex border-b border-gray-200 bg-gray-100/80 transition-colors ${
                                dragOverZone === 'header' ? 'bg-sky-100 border-sky-400' : ''
                            }`}
                            role="row"
                        >
                            {/* Expand Chevron Column Header — displayed ONLY when subgrids are attached */}
                            {activeSubgrids.length > 0 && (
                                <div className="w-10 flex-shrink-0 px-2 py-2 border-r border-gray-200 text-center font-bold text-gray-400 text-xs">
                                    
                                </div>
                            )}

                            {/* Dynamic Columns mapping */}
                            {columns.map((col, i) => {
                                const colTablePath = col.substring(0, col.lastIndexOf('.'));
                                const isNative = (colTablePath === syntaxPath);
                                const isInj = !isFsGrid && !isNative;
                                const colName = col.split('.').pop();
                                const isSlotHovered = dragOverSlot === i;

                                return (
                                    <div 
                                        key={col} 
                                        draggable={true}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('text/plain', JSON.stringify({
                                                type: 'COLUMN_NODE',
                                                columnKey: col,
                                                columnName: colName
                                            }));
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOverSlot(i);
                                        }}
                                        onDragLeave={() => setDragOverSlot(null)}
                                        onDrop={(e) => handleDropColumnAtSlot(e, i)}
                                        role="columnheader"
                                        style={{ width: columnWidths[col] || 150 }}
                                        className={`px-3 py-2 text-left text-xs font-semibold flex items-center justify-between border-r border-gray-200 select-none relative group cursor-grab active:cursor-grabbing transition-all ${
                                            isSlotHovered ? 'ring-2 ring-emerald-500 bg-emerald-100/90 z-30' : ''
                                        } ${
                                            isInj 
                                                ? 'italic font-serif text-indigo-700 bg-indigo-50/50 injected-col-header' 
                                                : 'font-normal not-italic text-gray-800 bg-gray-50 native-col-header'
                                        }`}
                                        title={`Full Syntax Train: ${col}`}
                                    >
                                        {/* Left drop line indicator */}
                                        <div className={`absolute -left-1 top-0 bottom-0 w-2 z-40 transition-all pointer-events-none ${
                                            isSlotHovered ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.8)]' : 'opacity-0'
                                        }`} />

                                        <span 
                                            className="cursor-pointer hover:text-blue-600 truncate flex-1"
                                            onClick={() => handleSort(col)}
                                        >
                                            {colName} {sortConfig.column === col ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                                        </span>
                                        
                                        <button 
                                            onClick={() => handleRemoveColumn(col)}
                                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 font-bold ml-1 text-[10px]"
                                            title="Delete column"
                                        >
                                            ✕
                                        </button>
                                        
                                        <div 
                                            onMouseDown={(e) => handleResizeMouseDown(e, col)}
                                            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-400 active:bg-blue-600 z-20"
                                        />
                                    </div>
                                );
                            })}
                            
                            {/* Drop zone at end of header row */}
                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragOverSlot(columns.length);
                                }}
                                onDragLeave={() => setDragOverSlot(null)}
                                onDrop={(e) => handleDropColumnAtSlot(e, columns.length)}
                                className={`px-3 py-2 text-xs font-bold border-2 border-dashed rounded transition-all cursor-pointer select-none flex items-center space-x-1 ${
                                    dragOverSlot === columns.length ? 'bg-emerald-200 border-emerald-500 text-emerald-800 scale-105' : 'bg-emerald-50/30 border-emerald-400/40 text-emerald-600 hover:bg-emerald-100/50'
                                }`}
                                title="Drop column here to append at end of grid"
                            >
                                <span>➕</span>
                                <span>Drop</span>
                            </div>
                        </div>

                        {/* Table Body Rows or Structure Mode ERD Blueprint Block */}
                        <div 
                            role="rowgroup" 
                            className={`divide-y divide-gray-200 transition-colors ${
                                dragOverZone === 'subgrid' ? 'bg-indigo-50/60 border-2 border-dashed border-indigo-400' : ''
                            }`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverZone('subgrid');
                            }}
                            onDragLeave={() => setDragOverZone(null)}
                            onDrop={handleSubgridBodyDrop}
                        >
                            {isStructureMode ? (
                                <div className="p-4 bg-slate-950 border border-amber-500/40 rounded-b-xl space-y-4">
                                    <div className="flex items-center justify-between border-b border-amber-500/30 pb-2">
                                        <div className="flex items-center space-x-2">
                                            <span className="text-amber-400 font-bold text-sm">📐 Structural Query Blueprint</span>
                                            <span className="text-xs font-mono text-amber-200 bg-amber-950 px-2 py-0.5 rounded border border-amber-800/60" title={`Full Syntax Train: ${syntaxPath}`}>
                                                {displayPathTitle}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-amber-400/80 flex items-center space-x-1">
                                            <span>🔑 PK:</span>
                                            <span className="text-amber-300 font-bold">{primaryKey || 'ID'}</span>
                                        </div>
                                    </div>

                                    {/* Structural Column Schema Map */}
                                    <div>
                                        <div className="text-[10px] text-amber-400 uppercase tracking-wider font-bold mb-1.5 flex items-center space-x-1">
                                            <span>📊 Header Schema Columns ({columns.length})</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {columns.map(col => {
                                                const colTablePath = col.substring(0, col.lastIndexOf('.'));
                                                const isNative = (colTablePath === syntaxPath);
                                                const isInj = !isFsGrid && !isNative;
                                                const colName = col.split('.').pop();
                                                return (
                                                    <div key={col} className={`px-2 py-1 rounded border text-[11px] flex items-center space-x-1.5 ${
                                                        isInj 
                                                            ? 'bg-indigo-950/80 border-indigo-500/60 text-indigo-300 italic font-serif' 
                                                            : 'bg-slate-900 border-amber-500/40 text-amber-100 font-normal not-italic'
                                                    }`} title={`Full Syntax Train: ${col}`}>
                                                        <span>{isInj ? '🌐' : '🔹'}</span>
                                                        <span className="font-semibold">{colName}</span>
                                                        <span className="text-[9px] opacity-60 font-mono">({col})</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Structural Subgrids Drop Targets */}
                                    <div>
                                        <div className="text-[10px] text-amber-400 uppercase tracking-wider font-bold mb-1.5 flex items-center space-x-1">
                                            <span>🔗 Attached Subgrid Relationships ({activeSubgrids.length})</span>
                                        </div>
                                        {activeSubgrids.length === 0 ? (
                                            <div 
                                                onDragOver={(e) => { e.preventDefault(); setDragOverZone('subgrid'); }}
                                                onDragLeave={() => setDragOverZone(null)}
                                                onDrop={handleSubgridBodyDrop}
                                                className={`p-4 border-2 border-dashed rounded-lg text-center transition-all cursor-pointer ${dragOverZone === 'subgrid' ? 'border-emerald-400 bg-emerald-950/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'border-amber-500/40 bg-amber-950/20 text-amber-300 hover:border-amber-400'}`}
                                            >
                                                <span className="font-bold text-amber-300">➕ Drag a Table or Column here to attach a Subgrid</span>
                                                <p className="text-[10px] text-amber-400/60 mt-1">Structural drop zone active in ERD mode</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {activeSubgrids.map(subgrid => {
                                                    const subPath = typeof subgrid === 'string' ? subgrid : subgrid.path;
                                                    const subId = subgrid.id || subgrid.instanceId || subPath;
                                                    return (
                                                        <div key={subId} className="p-1 bg-slate-900 border border-amber-500/40 rounded-lg">
                                                            <CanvasGrid
                                                                syntaxPath={subPath}
                                                                instanceId={subId}
                                                                parentRowId={null}
                                                                canvasConfig={canvasConfig}
                                                                setCanvasConfig={setCanvasConfig}
                                                                globalParameters={globalParameters}
                                                                token={token}
                                                                isStructureMode={true}
                                                                targetSchemaGroup={targetSchemaGroup}
                                                                onRemoveGrid={() => handleRemoveSubgrid(subId)}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : columns.length === 0 ? (
                                <div className="p-6 text-center text-slate-500 text-xs italic bg-slate-50 border-t border-slate-200 flex items-center justify-center space-x-2 font-medium">
                                    <span>📋</span>
                                    <span>No columns selected for this grid. Drag columns from Universe Explorer onto this grid, or click 'Manage Columns' to add columns.</span>
                                </div>
                            ) : data.length === 0 ? (
                                <div className="p-6 text-center text-gray-400 text-xs italic">
                                    No records found for this table query.
                                </div>
                            ) : (
                                data.map((row, rowIdx) => {
                                    const rowId = primaryKey && row[primaryKey] !== undefined ? row[primaryKey] : (row.id || rowIdx);
                                    const isRowExpanded = expandedRows[rowId] || false;

                                    return (
                                        <React.Fragment key={rowId}>
                                            <div className="flex hover:bg-gray-50/80 transition-colors" role="row">
                                                {/* Expand Chevron Cell — displayed ONLY when subgrids are attached */}
                                                {activeSubgrids.length > 0 && (
                                                    <div className="w-10 flex-shrink-0 px-2 py-2 border-r border-gray-200 text-center text-xs">
                                                        <button 
                                                            onClick={() => toggleRowExpand(rowId)}
                                                            className="text-gray-400 hover:text-blue-600 font-bold transition-colors"
                                                            title="Toggle nested subgrids"
                                                        >
                                                            {isRowExpanded ? '▼' : '▶'}
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Cells mapping */}
                                                {columns.map(col => {
                                                    const colStr = typeof col === 'string' ? col : String(col);
                                                    const baseCol = colStr.includes('.') ? colStr.split('.').pop() : colStr;
                                                    const cleanCol = colStr.replace(/^\[.*?\]/, '');
                                                    const noDbCol = cleanCol.includes('->') ? cleanCol.substring(cleanCol.indexOf('->') + 2) : cleanCol;

                                                    let rawVal = row[colStr];
                                                    if (rawVal === undefined) rawVal = row[cleanCol];
                                                    if (rawVal === undefined) rawVal = row[noDbCol];
                                                    if (rawVal === undefined) rawVal = row[baseCol];
                                                    if (rawVal === undefined) {
                                                        const normCol = colStr.replace(/\]\]/g, ']');
                                                        const matchKey = Object.keys(row).find(k => k === colStr || k === cleanCol || k === noDbCol || k.replace(/\]\]/g, ']') === normCol || k.endsWith(`.${baseCol}`) || k === baseCol);
                                                        if (matchKey) rawVal = row[matchKey];
                                                    }
                                                    const cellVal = (rawVal === undefined || rawVal === null) ? '' : rawVal;

                                                    return (
                                                        <div 
                                                            key={col} 
                                                            style={{ width: columnWidths[col] || 150 }}
                                                            className="px-3 py-2 text-xs text-gray-700 border-r border-gray-200 truncate select-text flex items-center justify-between"
                                                            title={`${baseCol}: ${String(cellVal)}`}
                                                        >
                                                            <span className="truncate font-sans">{String(cellVal)}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Subgrid row expansion */}
                                            {isRowExpanded && (
                                                <div className="bg-slate-50 border-b border-gray-300 p-4 pl-12 shadow-inner space-y-3">
                                                    {activeSubgrids.length === 0 ? (
                                                        <div className="text-xs text-gray-500 italic flex items-center space-x-2">
                                                            <span>ℹ️ No subgrids attached. Use '+ Add Subgrid' on the grid toolbar to add child/parent relationships.</span>
                                                        </div>
                                                    ) : (
                                                        activeSubgrids.map(subgrid => {
                                                            const subId = typeof subgrid === 'string' ? subgrid : (subgrid.instanceId || subgrid.id || subgrid.path);
                                                            const subLevel = canvasConfig?.levels?.[subId];
                                                            const subPath = subLevel?.syntaxPath || (typeof subgrid === 'string' ? subgrid : (subgrid.syntaxPath || subgrid.path));
                                                            
                                                            if (!subPath) return null;
                                                            return (
                                                                <div key={subId} className="bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                                                                    <CanvasGrid 
                                                                        syntaxPath={subPath}
                                                                        instanceId={subId}
                                                                        parentRowId={rowId}
                                                                        canvasConfig={canvasConfig}
                                                                        setCanvasConfig={setCanvasConfig}
                                                                        globalParameters={globalParameters}
                                                                        token={token}
                                                                        isStructureMode={false}
                                                                        targetSchemaGroup={targetSchemaGroup}
                                                                        onRemoveGrid={() => handleRemoveSubgrid(subId)}
                                                                    />
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>

                        {/* Pagination Bar */}
                        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-600">
                            <div>
                                Showing <span className="font-bold text-gray-800">{data.length}</span> of <span className="font-bold text-gray-800">{totalCount}</span> records
                            </div>
                            <div className="flex items-center space-x-2">
                                <button 
                                    disabled={page <= 1} 
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    className="px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-100 disabled:opacity-50 font-medium"
                                >
                                    Previous
                                </button>
                                <span>Page <span className="font-bold">{page}</span> of <span className="font-bold">{totalPages}</span></span>
                                <button 
                                    disabled={page >= totalPages} 
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    className="px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-100 disabled:opacity-50 font-medium"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Save Template Modal */}
            {showSaveTemplateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300]">
                    <div className="bg-white p-6 rounded-lg shadow-xl border border-gray-200 w-96 text-gray-900">
                        <h4 className="text-base font-bold mb-3">Save Grid Template</h4>
                        <div className="space-y-3 text-xs">
                            <div>
                                <label className="block font-bold text-gray-700 mb-1">Save Action</label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center space-x-1 cursor-pointer">
                                        <input 
                                            type="radio" 
                                            name="tplAction" 
                                            value="new" 
                                            checked={saveTemplateAction === 'new'} 
                                            onChange={() => setSaveTemplateAction('new')}
                                        />
                                        <span>Create New</span>
                                    </label>
                                    {activeTemplateId && (
                                        <label className="flex items-center space-x-1 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="tplAction" 
                                                value="update" 
                                                checked={saveTemplateAction === 'update'} 
                                                onChange={() => setSaveTemplateAction('update')}
                                            />
                                            <span>Update Existing</span>
                                        </label>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block font-bold text-gray-700 mb-1">Template Name</label>
                                <input 
                                    type="text" 
                                    value={newTemplateName} 
                                    onChange={e => setNewTemplateName(e.target.value)}
                                    placeholder="e.g. Default Sales View"
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block font-bold text-gray-700 mb-1">Description (Optional)</label>
                                <textarea 
                                    value={newTemplateDesc} 
                                    onChange={e => setNewTemplateDesc(e.target.value)}
                                    placeholder="Describe layout purposes..."
                                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none h-16"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-2 mt-4">
                            <button 
                                onClick={() => setShowSaveTemplateModal(false)}
                                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded font-medium"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSaveTemplate}
                                className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded font-bold"
                            >
                                Save Template
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
